import React from 'react';
import { Sparkles } from 'lucide-react';

/**
 * AI Component Family
 * All AI-related components use violet/purple accent
 * ONLY these components should use AI colors
 */

/**
 * AiButton - Button for AI actions
 */
export const AiButton = ({ children, icon: Icon = Sparkles, className = '', ...props }) => {
  return (
    <button
      className={`px-4 py-2.5 bg-gradient-to-r from-violet-50 to-purple-50 text-ai-primary border border-violet-200 rounded-lg font-semibold hover:from-violet-100 hover:to-purple-100 transition-colors flex items-center gap-2 ${className}`}
      {...props}
    >
      <Icon className="w-4 h-4" />
      {children}
    </button>
  );
};

/**
 * AiBadge - Badge for AI-generated content
 */
export const AiBadge = ({ children, className = '' }: { children?: any; className?: string }) => {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 bg-violet-50 text-ai-primary border border-violet-200 rounded-full text-xs font-medium ${className}`}>
      <Sparkles className="w-3 h-3" />
      {children || 'AI'}
    </span>
  );
};

/**
 * AiPanel - Dark panel for AI moments
 */
export const AiPanel = ({ children, title, className = '' }: { children: any; title?: string; className?: string }) => {
  return (
    <div className={`bg-gray-900 text-white rounded-lg p-6 ${className}`}>
      {title && (
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-violet-400" />
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
      )}
      {children}
    </div>
  );
};

/**
 * AiResult - Container for AI-generated results
 */
export const AiResult = ({ children, className = '' }) => {
  return (
    <div className={`bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-6 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-ai-primary flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * AiModal - Special modal for AI features
 */
export const AiModal = ({ children, isOpen, onClose, title }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-gray-900 text-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {title && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-semibold">{title}</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ×
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};
